import { useState, useRef, useEffect } from "react";
import { Alert } from "@mui/material";
import Modal from "../../../../components/Modal/Modal";
import DefaultButton from "../../../../components/simple/DefaultButton";
import { useAPI } from "../../../../hooks/useAPI";
import { useAuth } from "../../../../contexts/AuthContext";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentNumber: string;
  prefilledNewNumber?: string;
  twofaConsent?: boolean;
};

type Step = "enter_new" | "verify_current" | "confirm_new" | "success";

const NumberChangeModal = ({
  open,
  onClose,
  onSuccess,
  currentNumber,
  prefilledNewNumber = "",
  twofaConsent = false,
}: Props) => {
  const { callAPI } = useAPI();
  const { authToken } = useAuth();

  const [step, setStep] = useState<Step>("enter_new");
  const [changeToken, setChangeToken] = useState("");
  const [newNumber, setNewNumber] = useState("");
  const [maskedDest, setMaskedDest] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resetState = () => {
    setStep("enter_new");
    setChangeToken("");
    setNewNumber("");
    setMaskedDest("");
    setCode(["", "", "", "", "", ""]);
    setError("");
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    resetState();
    if (prefilledNewNumber) setNewNumber(prefilledNewNumber);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [open]);

  // Step 1: user submits their new number — sends OTP to current phone
  const handleRequestChange = async () => {
    if (!newNumber.trim()) {
      setError("Please enter your new mobile number.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await callAPI("user/request_number_change", {
        method: "POST",
        token: authToken,
        body: {
          new_mobile_number: newNumber.trim(),
          twofa_consent: twofaConsent,
        },
      });
      setChangeToken(res.change_token);
      setMaskedDest(res.code_sent_to || newNumber);
      setStep(
        res.next_step === "confirm_new" ? "confirm_new" : "verify_current",
      );
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      setError(err?.message || "Failed to send verification code.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value !== "" && !/^[0-9]$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newCode.every(Boolean) && index === 5) {
      submitCode(newCode.join(""));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
  };

  const submitCode = async (codeStr: string) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      if (step === "verify_current") {
        const res = await callAPI("user/verify_current_number", {
          method: "POST",
          token: authToken,
          body: { change_token: changeToken, verification_code: codeStr },
        });
        setChangeToken(res.change_token);
        setMaskedDest(res.code_sent_to || newNumber);
        setCode(["", "", "", "", "", ""]);
        setStep("confirm_new");
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else if (step === "confirm_new") {
        await callAPI("user/confirm_new_number", {
          method: "POST",
          token: authToken,
          body: { change_token: changeToken, verification_code: codeStr },
        });
        setStep("success");
      }
    } catch (err: any) {
      setError(err?.message || "Invalid or expired verification code.");
      setCode(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const codeStr = code.join("");
    if (codeStr.length === 6) submitCode(codeStr);
  };

  const stepTitle =
    step === "enter_new"
      ? "Enter your new number"
      : step === "verify_current"
        ? "Verify current number"
        : step === "confirm_new"
          ? "Verify new number"
          : "Number updated";

  const stepDescription =
    step === "enter_new"
      ? `Your current number is ${currentNumber || "not set"}. Enter the number you'd like to switch to.`
      : step === "verify_current"
        ? `Enter the code sent to your current phone number ending in ${maskedDest}`
        : step === "confirm_new"
          ? `Enter the code sent to your new phone number ending in ${maskedDest}`
          : "Your mobile number has been updated successfully.";

  return (
    <Modal
      id="number-change-modal"
      title="Change Phone Number"
      open={open}
      onClose={handleClose}
      showClose
      slim
    >
      <strong style={{ fontSize: "14px" }}>{stepTitle}</strong>
      <p style={{ margin: "5px 0 16px 0", fontSize: "14px" }}>
        {stepDescription}
      </p>

      {step === "enter_new" && (
        <>
          <input
            type="tel"
            value={newNumber}
            onChange={(e) => setNewNumber(e.target.value)}
            placeholder="+1 555 000 0000"
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #ccc",
              fontSize: "14px",
              boxSizing: "border-box",
              marginBottom: "16px",
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DefaultButton
            type="button"
            onClick={handleRequestChange}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send verification code"}
          </DefaultButton>
        </>
      )}

      {(step === "verify_current" || step === "confirm_new") && (
        <>
          <div
            style={{ display: "flex", gap: "8px", marginBottom: "16px" }}
            className="code-inputs"
          >
            {code.map((digit, i) => (
              <input
                key={i}
                type="tel"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                ref={(el) => (inputRefs.current[i] = el)}
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                disabled={loading}
                style={{
                  width: "40px",
                  height: "48px",
                  textAlign: "center",
                  fontSize: "20px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              />
            ))}
          </div>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DefaultButton
            type="button"
            onClick={handleManualSubmit}
            disabled={loading || code.some((d) => !d)}
            wideOnMobile
          >
            {loading ? "Verifying..." : "Verify"}
          </DefaultButton>
        </>
      )}

      {step === "success" && (
        <DefaultButton
          type="button"
          onClick={() => {
            onSuccess();
            resetState();
          }}
        >
          Done
        </DefaultButton>
      )}
    </Modal>
  );
};

export default NumberChangeModal;
